import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Car, CheckCircle, Bike, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const DriverApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    // Account fields (for new users)
    password: '',
    confirmPassword: '',
    // Driver application fields
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehiclePlateNumber: '',
    vehicleColor: '',
    vehicleType: 'standard'
  });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('tekstar_token');
    setIsLoggedIn(!!token);
    
    if (token) {
      // Check if user already has a driver application
      checkExistingApplication();
    }
  }, []);

  const checkExistingApplication = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/my-profile');
      
      if (response.data.driver) {
        // User already has an application
        navigate('/driver/dashboard');
      }
    } catch (error) {
      // No application found, user can proceed
      console.log('No existing application');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const api = (await import('../../utils/api')).default;
      
      // If user is not logged in, create account first
      if (!isLoggedIn) {
        // Validate password
        if (!formData.password || formData.password.length < 6) {
          toast({
            title: 'Invalid Password',
            description: 'Password must be at least 6 characters',
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        
        if (formData.password !== formData.confirmPassword) {
          toast({
            title: 'Passwords Do Not Match',
            description: 'Please make sure both passwords match',
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }

        // Create user account first
        const signupResponse = await api.post('/auth/signup', {
          name: formData.name,
          phone: `+260${formData.phone.replace(/\D/g, '')}`,
          email: formData.email || null,
          password: formData.password
        });

        // Store token and user data
        localStorage.setItem('tekstar_token', signupResponse.data.token);
        localStorage.setItem('tekstar_user', JSON.stringify(signupResponse.data.user));
        
        toast({
          title: 'Account Created!',
          description: 'Now submitting your driver application...',
        });
      }
      
      // Convert year to number and submit driver application
      const applicationData = {
        name: formData.name,
        email: formData.email.trim() || null,
        phone: `+260${formData.phone.replace(/\D/g, '')}`,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        vehicleYear: parseInt(formData.vehicleYear),
        vehiclePlateNumber: formData.vehiclePlateNumber,
        vehicleColor: formData.vehicleColor,
        vehicleType: formData.vehicleType
      };

      await api.post('/drivers/apply', applicationData);

      setSubmitted(true);
      toast({
        title: 'Application Submitted!',
        description: 'Your driver application has been submitted for review.',
      });

      setTimeout(() => {
        navigate('/driver/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Application error:', error);
      // Handle Pydantic validation errors (detail is an array)
      let errorMessage = 'Failed to submit application';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast({
        title: 'Application Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for applying to become a Tekstar driver. Your application is under review.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to dashboard...
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Become a Tekstar Driver</h1>
          <p className="text-gray-600">
            {isLoggedIn 
              ? 'Complete your driver application' 
              : 'Create an account and apply to drive'}
          </p>
          {isLoggedIn && (
            <p className="text-sm text-green-600 mt-2">✓ Logged in - just complete your application below</p>
          )}
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Creation Section - Only show if not logged in */}
            {!isLoggedIn && (
              <div className="pb-6 border-b border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center justify-center w-6 h-6 bg-pink-600 text-white text-sm font-bold rounded-full">1</span>
                  <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
                </div>
                <p className="text-sm text-gray-500 mb-4">Already have an account? <Link to="/driver/login" className="text-pink-600 hover:underline">Sign in here</Link></p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!isLoggedIn}
                        minLength={6}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Minimum 6 characters</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required={!isLoggedIn}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 bg-pink-600 text-white text-sm font-bold rounded-full">
                  {isLoggedIn ? '1' : '2'}
                </span>
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
                    <Input
                      id="phone"
                      type="tel"
                      className="rounded-l-none"
                      placeholder="XXX XXX XXX"
                      maxLength={9}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 bg-pink-600 text-white text-sm font-bold rounded-full">
                  {isLoggedIn ? '2' : '3'}
                </span>
                <h2 className="text-xl font-semibold text-gray-900">Vehicle Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicleMake">Vehicle Make *</Label>
                  <Input
                    id="vehicleMake"
                    placeholder="e.g., Toyota"
                    value={formData.vehicleMake}
                    onChange={(e) => setFormData({ ...formData, vehicleMake: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleModel">Vehicle Model *</Label>
                  <Input
                    id="vehicleModel"
                    placeholder="e.g., Camry"
                    value={formData.vehicleModel}
                    onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleYear">Year *</Label>
                  <Input
                    id="vehicleYear"
                    type="number"
                    placeholder="2020"
                    min="2010"
                    max={new Date().getFullYear() + 1}
                    value={formData.vehicleYear}
                    onChange={(e) => setFormData({ ...formData, vehicleYear: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehiclePlateNumber">License Plate *</Label>
                  <Input
                    id="vehiclePlateNumber"
                    placeholder="ABC-1234"
                    value={formData.vehiclePlateNumber}
                    onChange={(e) => setFormData({ ...formData, vehiclePlateNumber: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleColor">Vehicle Color *</Label>
                  <Input
                    id="vehicleColor"
                    placeholder="e.g., Black"
                    value={formData.vehicleColor}
                    onChange={(e) => setFormData({ ...formData, vehicleColor: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleType">Vehicle Type *</Label>
                  <select
                    id="vehicleType"
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                    required
                  >
                    <option value="">Select vehicle type</option>
                    <option value="motorbike">Motor Bike / Motorcycle</option>
                    <option value="standard">Standard Car (4 seats)</option>
                    <option value="xl">XL Vehicle (6+ seats)</option>
                    <option value="luxury">Luxury Vehicle</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Documents Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Required Documents</h3>
              <p className="text-sm text-blue-800 mb-2">
                After submitting your application, you will need to provide:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                <li>Valid driver license</li>
                <li>Vehicle registration</li>
                <li>Insurance documents</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                (Document upload will be available after admin review)
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
              disabled={loading}
            >
              {loading ? 'Submitting Application...' : 'Submit Application'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default DriverApplication;